import { RequestBuilder } from './request-builder';
import { DatasetClientFactory } from '../services';
import { QueryModelsStrategies } from './query-model';

/**
 * @typedef {import('@goodt-common/data').IDatasetQuery} IDatasetQuery
 * @typedef {import('@goodt-common/data').IDatasetRequestProps} IDatasetRequestProps
 * @typedef {import('@goodt-common/data').QueryModel} QueryModel
 * @typedef {import('@goodt-common/data').QueryModelExtended} QueryModelExtended
 * @typedef {import('@goodt-common/data').QueryModelWithMetricsCustom} QueryModelWithMetricsCustom
 * @typedef {import('@goodt-common/data').IQueryModelStrategy} IQueryModelStrategy
 * @typedef {QueryModelExtended|QueryModel|QueryModelWithMetricsCustom} QueryModelOneOf
 */

export class Request {
    /** @type {RequestBuilder} */
    _requestBuilder;

    /**
     *
     * @type {number}
     */
    offset = 0;

    /**
     * @type {boolean}
     * @private
     */
    _isLoading = false;

    /**
     * @param {IDatasetRequestProps} options
     * @param {{ client: import('@goodt-common/data').DataClient, queryModelStrategy: IQueryModelStrategy  }} extraOptions
     */
    constructor(options, { client, queryModelStrategy = QueryModelsStrategies.DEFAULT } = {}) {
        this._requestBuilder = new RequestBuilder(options, { queryModelStrategy });
        this._client = client;
    }

    /**
     *
     * @return {string}
     */
    get name() {
        // eslint-disable-next-line goodt-rules/no-long-prop-chains
        return this._requestBuilder.options.name;
    }

    /**
     *
     * @return {import('./types').IDatasetRequestProps['limit']}
     */
    get limit() {
        // eslint-disable-next-line goodt-rules/no-long-prop-chains
        return this._requestBuilder.options.limit;
    }

    /**
     *
     * @param {number} limit
     */
    set limit(limit) {
        // eslint-disable-next-line goodt-rules/no-long-prop-chains
        this._requestBuilder.options.limit = limit;
    }

    /**
     * @returns {import('@goodt-common/data').QueryModel}
     */
    get query() {
        return this._requestBuilder.queryModel;
    }

    /**
     * @return {boolean}
     */
    get isLoading() {
        return this._isLoading;
    }

    /**
     * @param {import('@goodt-common/data').IDatasetRequestProps} options
     */
    set options(options) {
        this._requestBuilder.options = options;
    }

    /**
     *
     * @param {boolean} debug
     * @return {Promise<import('@goodt-common/data').IDatasetResult>}
     */
    async send({ debug } = {}) {
        const request = this._requestBuilder.build();
        const { offset } = this;
        this._isLoading = true;
        try {
            const result = await this._client.getData(request, { offset, debug });
            return this._processResult(result);
        } finally {
            this._isLoading = false;
        }
    }

    /**
     *
     * @param {import('@goodt-common/data').IDatasetResult} result
     * @return {import('@goodt-common/data').IDatasetResult}
     * @private
     */
    _processResult(result) {
        if (result.rows == null) {
            return result;
        }

        const orderMapEntries = Object.entries(this.query.createResultQueryParamMap());
        // @NOTE optimization: using "for" instead reduce/map
        for (let i = 0; i < result.rows.length; i++) {
            const row = result.rows[i];
            const processedRow = orderMapEntries.reduce((_row, [queryParam, resultParam]) => {
                // eslint-disable-next-line no-param-reassign
                _row[queryParam] = row[resultParam];
                return _row;
            }, {});
            // @NOTE reuse result rows to reduce memory (no duplicating result rows via map/reduce)
            // eslint-disable-next-line no-param-reassign
            result.rows[i] = Object.freeze(processedRow);
        }

        return result;
    }

    /**
     *
     */
    cancel() {
        this._client.cancelActiveRequests();
    }
}

/**
 * @type {import('./request').DatasetRequestFactory}
 */
export const DatasetRequestFactory = (requestConfig, { clientConfig, queryModelStrategy } = {}) => {
    const client = DatasetClientFactory(clientConfig);
    return new Request(requestConfig, { client, queryModelStrategy });
};
